import React, { useCallback } from 'react';

import { type ForgeDoc } from '@atlassian/forge-ui-types';

import { RendererNext } from '..';
import { type UIKitRendererProps } from './types';
import { useUIKitComponents } from './useUIKitComponents';

export type EmbeddedUIKitModalProps = Omit<
	UIKitRendererProps,
	| 'contextIds'
	| 'entryPoint'
	| 'getContextToken'
	| 'locale'
	| 'extensionViewData'
	| 'viewOnClose'
	| 'extensionData'
> & {
	/**
	 * The ForgeDoc to render — provided externally via the setForgeDoc bridge method
	 * called from the Custom UI iframe.
	 */
	forgeDoc: ForgeDoc | undefined;
};

export const EmbeddedUIKitModal = (props: EmbeddedUIKitModalProps): React.JSX.Element | null => {
	const {
		accountId,
		bridge,
		client,
		components,
		coreData,
		extension,
		forgeDoc,
		loadingComponent,
		mentionProvider,
		modalExtension,
		onAppError,
		onError,
		onSuccess,
		product,
	} = props;

	const { cloudId, workspaceId } = coreData;

	const getComponents = useUIKitComponents({
		accountId,
		bridge,
		client,
		cloudId,
		workspaceId,
		mentionProvider,
		product,
		components,
	});

	const handleError = useCallback(
		(err: Error) => {
			onError?.(err.message);
			onAppError?.(err.message);
		},
		[onError, onAppError],
	);

	if (!forgeDoc) {
		return null;
	}

	return (
		<RendererNext
			forgeDoc={forgeDoc}
			extension={extension}
			components={getComponents}
			modalExtension={modalExtension}
			extensionType={'xen:macro'}
			loading
			loadingComponent={loadingComponent}
			onError={handleError}
			client={client}
			onSuccess={onSuccess}
		/>
	);
};
